import { useEffect, useEffectEvent, useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Form, Tag } from 'antd'
import { ChevronDown } from 'lucide-react'
import {
  CUSTOMER_SEARCH_BATCH_ASYNC_THRESHOLD,
  type CustomerListInput,
  type CustomerSearchBatchAction
} from '@super-mega/server/contracts/customer'
import { AuthorizedDropdown } from '@/features/authorization/authorized-dropdown.tsx'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { CUSTOMER_BATCH_ACTIONS, CUSTOMER_BATCH_MENU_ITEMS } from '../const/customer-batch-actions.ts'
import { CustomerBatchProgressModal } from './customer-batch-progress-modal.tsx'
import { CustomerMonthlyBillingFields } from './customer-monthly-billing-fields.tsx'
import { CustomerTagSelect } from './customer-tag-select.tsx'

type Values = Omit<Extract<CustomerSearchBatchAction, { action: 'monthly-billing' }>, 'action'> & { tagIds?: number[] }
type Target = { action: CustomerSearchBatchAction['action']; search: CustomerListInput; count: number }

export function CustomerSearchBatchActions({
  search,
  total,
  disabled,
  onSaved
}: {
  search: CustomerListInput
  total: number
  disabled: boolean
  onSaved: () => void
}) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message } = App.useApp()
  const [target, setTarget] = useState<Target>()
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [jobId, setJobId] = useState<string>()
  const [showProgress, setShowProgress] = useState(false)
  const mutation = useMutation(trpc.customer.batchBySearch.mutationOptions())
  const status = useQuery({
    ...trpc.customer.batchBySearchStatus.queryOptions({ jobId: jobId ?? '' }),
    enabled: Boolean(jobId),
    refetchInterval: (query) =>
      ['completed', 'failed', 'missing'].includes(query.state.data?.state ?? '') ? false : 3000
  })
  const jobRunning = Boolean(jobId) && !['completed', 'failed', 'missing'].includes(status.data?.state ?? '')
  const refresh = async () => {
    await Promise.all([
      cache.invalidateQueries(trpc.customer.list.queryFilter()),
      cache.invalidateQueries(trpc.customer.billingCalendar.queryFilter())
    ])
    onSaved()
  }
  const onFinished = useEffectEvent(() => {
    void refresh()
  })
  useEffect(() => {
    if (status.data?.state === 'completed' || status.data?.state === 'failed') {
      onFinished()
    }
  }, [jobId, status.data?.state])
  const action = target ? CUSTOMER_BATCH_ACTIONS[target.action] : undefined
  const willQueue = Boolean(target && target.count > CUSTOMER_SEARCH_BATCH_ASYNC_THRESHOLD)
  let submitText = '确认执行'
  if (willQueue) submitText = '提交后台任务'
  if (mutation.data) submitText = '处理完成'

  return (
    <>
      <AuthorizedDropdown
        resource="customer"
        trigger={['click']}
        placement="bottomRight"
        menu={{
          items: CUSTOMER_BATCH_MENU_ITEMS,
          onClick: ({ key }) => {
            const chosen = CUSTOMER_BATCH_MENU_ITEMS.find((item) => item.key === key)
            if (!chosen) {
              return
            }
            mutation.reset()
            setShowProgress(false)
            setTarget({ action: key as Target['action'], search: { ...search }, count: total })
            setConfirmationOpen(true)
          }
        }}
      >
        <Button disabled={disabled || total === 0 || mutation.isPending || jobRunning} loading={mutation.isPending}>
          按搜索批量操作 <ChevronDown aria-hidden size={14} />
        </Button>
      </AuthorizedDropdown>
      {jobId ? <Button onClick={() => setShowProgress(true)}>查看批量任务</Button> : null}
      {target ? (
        <ModalForm<Values>
          open={confirmationOpen}
          title={action?.label}
          layout="vertical"
          width="34rem"
          disabled={mutation.isPending || Boolean(mutation.data)}
          initialValues={{ tagIds: [], autoCollectionBillGrouping: false, autoSendCollectionBillGroup: false }}
          modalProps={{
            destroyOnHidden: true,
            mask: { closable: false },
            closable: !mutation.isPending,
            keyboard: !mutation.isPending,
            afterClose: () => {
              setTarget(undefined)
              if (mutation.data?.queued) setShowProgress(true)
            }
          }}
          onOpenChange={(open) => {
            if (!mutation.isPending) setConfirmationOpen(open)
          }}
          submitter={{
            searchConfig: { submitText, resetText: mutation.data ? '关闭' : '取消' },
            resetButtonProps: { disabled: mutation.isPending },
            submitButtonProps: { danger: action?.danger, loading: mutation.isPending, disabled: Boolean(mutation.data) }
          }}
          onFinish={async (values) => {
            if (mutation.isPending) {
              return false
            }
            let operation: CustomerSearchBatchAction
            if (target.action === 'add-tags') {
              operation = { action: target.action, tagIds: values.tagIds ?? [] }
            } else if (target.action === 'monthly-billing') {
              operation = {
                action: target.action,
                autoCollectionBillGrouping: values.autoCollectionBillGrouping,
                collectionBillGroupingDay: values.collectionBillGroupingDay ?? null,
                autoSendCollectionBillGroup: values.autoSendCollectionBillGroup,
                collectionBillGroupingFrom: values.collectionBillGroupingFrom
              }
            } else {
              operation = { action: target.action }
            }
            try {
              const result = await mutation.mutateAsync({
                search: target.search,
                expectedCount: target.count,
                operation
              })
              if (result.queued) {
                setJobId(result.jobId)
                setConfirmationOpen(false)
                void message.success(`已提交 ${result.count} 个客户到后台任务`)
              } else {
                const summary = `处理完成：成功 ${result.successCount} 个，失败 ${result.failedCount} 个`
                if (result.failedCount) {
                  void message.warning(summary)
                } else {
                  void message.success(summary)
                }
                await refresh()
                // 保留失败详情，避免关窗后只剩短暂提示。
                if (!result.failedCount) {
                  setConfirmationOpen(false)
                }
              }
              return false
            } catch (error) {
              void message.error(getErrorMessage(error, '按搜索批量操作失败'))
              return false
            }
          }}
        >
          <div className="border-border bg-surface-muted mb-5 rounded-lg border p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <span className="text-content-muted text-sm">当前搜索结果</span>
              <Tag className="m-0" color={willQueue ? 'processing' : 'default'}>
                {willQueue ? '后台异步执行' : '直接执行'}
              </Tag>
            </div>
            <div className="mb-1 flex items-baseline gap-1.5">
              <strong className="text-2xl font-semibold tabular-nums">{target.count.toLocaleString()}</strong>
              <span className="text-content-muted text-sm">个客户</span>
            </div>
            <p className="text-content-muted m-0 text-sm">使用已提交的搜索条件，包含所有分页的匹配客户。</p>
          </div>
          <Alert className="mb-5!" showIcon type={action?.danger ? 'warning' : 'info'} title={action?.description} />
          {target.action === 'add-tags' ? (
            <Form.Item
              name="tagIds"
              label="客户标签"
              rules={[{ required: true, type: 'array', min: 1, max: 50, message: '请选择 1～50 个客户标签' }]}
            >
              <CustomerTagSelect disabled={mutation.isPending} />
            </Form.Item>
          ) : null}
          {target.action === 'monthly-billing' ? (
            <CustomerMonthlyBillingFields compact disabled={mutation.isPending || Boolean(mutation.data)} />
          ) : null}
          <p className="text-content-muted mb-0 text-xs leading-5">
            {willQueue
              ? `超过 ${CUSTOMER_SEARCH_BATCH_ASYNC_THRESHOLD} 个客户，将提交后台任务，可关闭窗口后查看进度。`
              : `不超过 ${CUSTOMER_SEARCH_BATCH_ASYNC_THRESHOLD} 个客户，确认后直接执行。`}
            {' 每 100 个客户为一批，失败批次不影响后续批次。'}
          </p>
          {mutation.data?.warnings.length ? (
            <Alert
              className="mt-4"
              type="warning"
              showIcon
              title="部分客户处理失败"
              description={
                <ul>
                  {mutation.data.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              }
            />
          ) : null}
        </ModalForm>
      ) : null}
      <CustomerBatchProgressModal
        open={showProgress}
        status={status.data}
        isError={status.isError}
        onRetry={() => void status.refetch()}
        onClose={() => setShowProgress(false)}
      />
    </>
  )
}
